import { useState } from 'react';

import { Button, ButtonGroup, Flex } from '@chakra-ui/react';

import { PaginationState } from '@tanstack/react-table';

import ReconciliationRunDetailsGate from 'app/components/Reconciliations/ReconciliationRunDetailsGate';
import ReconciliationPagedTable from 'app/components/Reconciliations/ReconciliationPagedTable';

import { defaultPageSize } from 'app/constants/table';

import useFetchReconciliationCompanies from 'app/hooks/useFetchReconciliationCompanies';

import { ReconciliationCompanyStatus } from 'app/types/reconciliations';

import getReconciliationCompanyColumns from 'app/utils/table/reconciliationCompanies';

const STATUS_OPTIONS: { value?: ReconciliationCompanyStatus; label: string }[] = [
  { value: undefined, label: 'All' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CHECKED', label: 'Checked' },
];

interface ReconciliationCompaniesTabProps {
  reconciliationId: string;
  companyId?: string;
  status?: ReconciliationCompanyStatus;
  onStatusChange: (status?: ReconciliationCompanyStatus) => void;
  isRunning: boolean;
  detailsAvailable: boolean;
}

/** Lists the companies a reconciliation run checked or failed to check, with why a company failed. */
function ReconciliationCompaniesTab({
  reconciliationId,
  companyId = undefined,
  status = undefined,
  onStatusChange,
  isRunning,
  detailsAvailable,
}: ReconciliationCompaniesTabProps) {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  const { data, isLoading, isError } = useFetchReconciliationCompanies(
    {
      id: reconciliationId,
      companyId,
      status,
      page: pagination.pageIndex,
      size: pagination.pageSize,
    },
    !isRunning && detailsAvailable
  );
  const page =
    data && companyId
      ? { ...data, list: data.list.filter((company) => company.companyId === companyId) }
      : data;

  return (
    <ReconciliationRunDetailsGate
      isRunning={isRunning}
      detailsAvailable={detailsAvailable}
      pendingText="Its companies"
    >
      <Flex flexDir="column" gap="20px" w="full">
        <ButtonGroup
          size="sm"
          isAttached
          variant="outline"
          data-testid="reconciliation-companies-status"
        >
          {STATUS_OPTIONS.map((option) => (
            <Button
              key={option.label}
              isActive={status === option.value}
              onClick={() => {
                onStatusChange(option.value);
                setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
              }}
              data-testid={`reconciliation-companies-status-${option.label.toLowerCase()}`}
            >
              {option.label}
            </Button>
          ))}
        </ButtonGroup>

        <ReconciliationPagedTable
          page={page}
          isLoading={isLoading}
          isError={isError}
          columns={getReconciliationCompanyColumns(reconciliationId)}
          pagination={pagination}
          onPaginationChange={setPagination}
          getRowId={(company) => company.companyId}
          emptyText={status === 'FAILED' ? 'No company failed in this run' : 'No companies to show'}
          errorText="Sorry, we failed to fetch the companies. Please try again."
          testId="reconciliation-companies"
        />
      </Flex>
    </ReconciliationRunDetailsGate>
  );
}

export default ReconciliationCompaniesTab;
